import React, { useEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { ZIMBABWE_CITY_CENTERS } from '../utils/jobLocation';

// Fix default marker icon broken by webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface MapPickerProps {
  latitude: number | null;
  longitude: number | null;
  onChange?: (lat: number, lng: number) => void;
  onSelectAddress?: (address: string) => void;
  height?: number;
  readOnly?: boolean;
}

// Zimbabwe center + bounds
const ZW_CENTER: [number, number] = [-19.0154, 29.1549];
const ZW_BOUNDS: L.LatLngBoundsExpression = [[-22.5, 25.2], [-15.3, 33.1]];

type LocationResult = { display_name: string; lat: string; lon: string };

const getLocalLocationResults = (query: string): LocationResult[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];
  return ZIMBABWE_CITY_CENTERS
    .filter((place) => place.city.toLowerCase().includes(normalizedQuery))
    .slice(0, 5)
    .map((place) => ({
      display_name: `${place.city}, Zimbabwe`,
      lat: String(place.latitude),
      lon: String(place.longitude),
    }));
};

export const MapPicker: React.FC<MapPickerProps> = ({
  latitude,
  longitude,
  onChange,
  onSelectAddress,
  height = 280,
  readOnly = false,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LocationResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<{ lat: number; lng: number; address?: string } | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const previewLocation = (lat: number, lng: number, address?: string) => {
    setPendingLocation({ lat, lng, address });
    if (!mapRef.current) return;
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng]).addTo(mapRef.current);
    }
    markerRef.current.bindPopup(address || 'Survey location').openPopup();
    mapRef.current.setView([lat, lng], 14);
  };

  const confirmLocation = () => {
    if (!pendingLocation) return;
    onChange?.(parseFloat(pendingLocation.lat.toFixed(6)), parseFloat(pendingLocation.lng.toFixed(6)));
    if (pendingLocation.address) onSelectAddress?.(pendingLocation.address);
    setPendingLocation(null);
  };

  const searchLocations = async () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    
    setIsSearching(true);
    setSearchError(null);
    setResults([]);

    const coordMatch = trimmedQuery.match(/^(-?\d+(\.\d+)?)[,\s]+(-?\d+(\.\d+)?)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lon = parseFloat(coordMatch[3]);
      try {
        const revUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`;
        const revRes = await fetch(revUrl, { headers: { 'User-Agent': 'SurveyConnect/1.0' } });
        const revData = await revRes.json();
        const displayName = revData.display_name || `Coordinate: ${lat.toFixed(5)}, ${lon.toFixed(5)}`;
        setResults([{ display_name: displayName, lat: String(lat), lon: String(lon) }]);
      } catch (err) {
        setResults([{ display_name: `Coordinate: ${lat.toFixed(5)}, ${lon.toFixed(5)}`, lat: String(lat), lon: String(lon) }]);
      }
      setIsSearching(false);
      return;
    }

    const localResults = getLocalLocationResults(trimmedQuery);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=zw&q=${encodeURIComponent(trimmedQuery)}`;
      const response = await fetch(url, { headers: { 'User-Agent': 'SurveyConnect/1.0' } });
      if (!response.ok) throw new Error('Network error');
      const data = await response.json();
      const remoteResults: LocationResult[] = Array.isArray(data) ? data : [];
      
      const merged = [...localResults, ...remoteResults].filter((result, index, items) =>
        items.findIndex((item) => item.display_name === result.display_name) === index,
      );
      
      if (merged.length === 0) {
        setSearchError(`No locations found for "${trimmedQuery}". Try another spelling or use coordinates.`);
      } else {
        setResults(merged.slice(0, 6));
      }
    } catch (err) {
      if (localResults.length > 0) {
        setResults(localResults);
      } else {
        setSearchError('Network error occurred. Please check your connection or try coordinates.');
      }
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: (latitude != null && longitude != null)
        ? [latitude, longitude]
        : ZW_CENTER,
      zoom: (latitude != null && longitude != null) ? 14 : 6,
      maxBounds: ZW_BOUNDS,
      maxBoundsViscosity: 0.8,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    // Existing marker
    if (latitude != null && longitude != null) {
      const marker = L.marker([latitude, longitude]).addTo(map);
      marker.bindPopup('Survey location').openPopup();
      markerRef.current = marker;
    }

    if (!readOnly) {
      map.on('click', (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;
        previewLocation(lat, lng, `Selected location: ${lat.toFixed(5)}, ${lng.toFixed(5)}`);
      });
    }

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // Update marker when coords change externally (e.g. GPS button)
  useEffect(() => {
    if (!mapRef.current || latitude == null || longitude == null) return;

    setPendingLocation(null);
    if (markerRef.current) {
      markerRef.current.setLatLng([latitude, longitude]);
    } else {
      markerRef.current = L.marker([latitude, longitude]).addTo(mapRef.current);
    }
    markerRef.current.bindPopup('Survey location').openPopup();
    mapRef.current.setView([latitude, longitude], 14);
  }, [latitude, longitude]);

  return (
    <div style={{ width: '100%', height, borderRadius: 10, overflow: 'hidden', border: '1px solid #E8ECF0', position: 'relative' }}>
      {!readOnly && (
        <div style={{
          position: 'absolute',
          top: 10,
          left: 10,
          right: 10,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          pointerEvents: 'auto',
        }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  searchLocations();
                }
              }}
              placeholder="Search area, suburb, town..."
              style={{
                flex: 1,
                height: 38,
                border: '1px solid var(--sc-border, #E8ECF0)',
                borderRadius: 8,
                padding: '0 10px',
                fontSize: 13,
                background: 'var(--sc-bg-secondary, #FFF)',
                color: 'var(--sc-text-primary, #1F2937)',
                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.12)',
              }}
            />
            <button
              type="button"
              onClick={searchLocations}
              style={{
                height: 38,
                border: 'none',
                borderRadius: 8,
                padding: '0 12px',
                fontSize: 12,
                fontWeight: 700,
                background: 'var(--sc-brand-primary, #0C5ADB)',
                color: '#FFF',
                cursor: 'pointer',
              }}
            >
              {isSearching ? '...' : 'Search'}
            </button>
          </div>
          {searchError && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', padding: '8px 10px', borderRadius: 8, fontSize: 12 }}>
              {searchError}
            </div>
          )}
          {results.length > 0 && (
            <div style={{ background: 'var(--sc-bg-secondary, #FFF)', border: '1px solid var(--sc-border, #E8ECF0)', borderRadius: 8, overflow: 'hidden', boxShadow: '0 8px 22px rgba(15, 23, 42, 0.16)' }}>
              {results.map((result) => (
                <button
                  key={`${result.lat}-${result.lon}-${result.display_name}`}
                  type="button"
                  onClick={() => {
                    const lat = Number(result.lat);
                    const lng = Number(result.lon);
                    setQuery(result.display_name);
                    setResults([]);
                    previewLocation(lat, lng, result.display_name);
                  }}
                  style={{
                    width: '100%',
                    border: 'none',
                    borderBottom: '1px solid var(--sc-border-light, #EEF2F7)',
                    background: 'transparent',
                    textAlign: 'left',
                    padding: '9px 10px',
                    fontSize: 12,
                    color: 'var(--sc-text-primary, #1F2937)',
                    cursor: 'pointer',
                  }}
                >
                  {result.display_name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {!readOnly && (
        <div style={{
          position: 'absolute', bottom: 10, left: 10, right: 10,
          display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'rgba(17, 24, 39, 0.84)', color: '#FFF', padding: '6px 12px',
            borderRadius: 20, fontSize: 12, fontWeight: 600, pointerEvents: 'none',
          }}>
            {pendingLocation ? 'Location selected' : 'Click on the map to set survey location'}
          </div>
          {pendingLocation && (
            <button
              type="button"
              onClick={confirmLocation}
              style={{
                height: 34,
                border: 'none',
                borderRadius: 8,
                padding: '0 12px',
                fontSize: 12,
                fontWeight: 700,
                background: 'var(--sc-brand-primary, #0C5ADB)',
                color: '#FFF',
                cursor: 'pointer',
                boxShadow: '0 6px 16px rgba(15, 23, 42, 0.2)',
              }}
            >
              Confirm location
            </button>
          )}
        </div>
      )}
    </div>
  );
};
